import { useMemo, useState } from 'react'

import { ComboBox, ComboBoxItem, Props as ComboBoxProps } from './ComboBox'

type SearchableSelectItem = ComboBoxItem & { searchableText?: string }

// We expose a selectedItemID prop here instead of selectedItem to make it a bit
// more ergonomic to use in the common case. However, there are some cases where
// we need to explicitly set the selected item because it may have different
// properties when selected than when it is presented in the dropdown. selectedItemOverride
// is available for that use case.
type Props<ItemType extends SearchableSelectItem> = Omit<
    ComboBoxProps<ItemType>,
    'selectedItem'
> & { selectedItemID?: string | number; selectedItemOverride?: ItemType | null }

export const SearchableSelect = <ItemType extends SearchableSelectItem>({
    placeholder,
    ...props
}: Props<ItemType>) => {
    const [searchValue, setSearchValue] = useState('')

    const effectiveSearchValue = props.searchValue ?? searchValue
    const effectiveOnSearchValueChange = props.onSearchValueChange ?? setSearchValue

    const filteredItems = useMemo(() => {
        const queryTokens = effectiveSearchValue.toLowerCase().split(/\s+/) ?? []

        return props.items?.filter((item) => {
            const searchableText = (item.searchableText ?? item.title).toLowerCase()
            return queryTokens.every((token) => searchableText.includes(token))
        })
    }, [props.items, effectiveSearchValue])

    const selectedItem = useMemo(() => {
        if (props.selectedItemOverride !== undefined) {
            return props.selectedItemOverride
        }
        if (props.selectedItemID === undefined || props.items === undefined) {
            return null
        }
        return props.items.find((item) => item.id === props.selectedItemID) ?? null
    }, [props.selectedItemID, props.items, props.selectedItemOverride])

    return (
        <ComboBox
            {...props}
            items={filteredItems}
            placeholder={placeholder ?? 'Type to search'}
            selectedItem={selectedItem}
            searchValue={effectiveSearchValue}
            onSearchValueChange={effectiveOnSearchValueChange}
            onClearSearchValue={() => effectiveOnSearchValueChange('')}
        />
    )
}
